/**
 * Reproductor de vídeo: modal centrado o pantalla anclada a un objeto del mapa.
 *
 * El motor sólo pide que se reproduzca algo (`videoRequested`); aquí se decide
 * cómo. En modo `tv` la caja sigue a la cámara cada fotograma, así que el vídeo
 * parece pegado al escenario aunque el jugador se mueva.
 *
 * YouTube está bloqueado en muchas escuelas, así que la descripción del vídeo se
 * muestra siempre: si el iframe no carga, el contenido sigue llegando.
 */

import { button, el, on, type El } from './dom';

export interface VideoSpec {
  video: string;
  titulo?: string;
  descripcion?: string;
  modo: 'modal' | 'tv';
  objeto?: string;
  /** Ancho de la pantalla como fracción del ancho del mapa. */
  ancho: number;
  poster?: string;
}

/** Caja en píxeles CSS relativa al contenedor que aloja el reproductor. */
export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface VideoViewOptions {
  onClose: () => void;
  /** URL ya resuelta del póster declarado en los assets del juego. */
  posterSrc?: string;
  /** Proyecta el objeto ancla a pantalla; null si no existe o está fuera de cuadro. */
  anchor?: (objeto: string, ancho: number) => ScreenRect | null;
}

/** Sin `anchor` no hay modo tv posible: mejor un modal que nada. */
function embedUrl(id: string): string {
  const params = new URLSearchParams({ autoplay: '1', rel: '0', modestbranding: '1', playsinline: '1' });
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?${params.toString()}`;
}

export class VideoView {
  private root: El<'div'> | null = null;
  private offEscape: (() => void) | null = null;
  private rafId: number | null = null;

  constructor(
    private readonly host: HTMLElement,
    private readonly spec: VideoSpec,
    private readonly opts: VideoViewOptions
  ) {}

  private get asTv(): boolean {
    return this.spec.modo === 'tv' && !!this.spec.objeto && !!this.opts.anchor;
  }

  open(): void {
    if (this.root) return;
    this.root = this.asTv ? this.buildTv() : this.buildModal();
    this.host.append(this.root);
    this.offEscape = on(document, 'keydown', (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') this.close();
    });
    if (this.asTv) this.startAnchorLoop();
  }

  private close(): void {
    this.destroy();
    this.opts.onClose();
  }

  destroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.offEscape?.();
    this.offEscape = null;
    // Quitar el iframe de golpe corta la reproducción: si sólo se oculta, el
    // audio de YouTube sigue sonando encima del juego.
    this.root?.remove();
    this.root = null;
  }

  /* ------------------------------- estructura ------------------------------- */

  private buildPlayer(): El<'div'> {
    const frame = el('div', { className: 'ae-video-frame' });
    if (this.opts.posterSrc) {
      const poster = el('img', {
        className: 'ae-video-poster',
        src: this.opts.posterSrc,
        alt: this.spec.titulo ?? 'Vídeo'
      });
      frame.append(poster);
    }
    const iframe = el('iframe', {
      className: 'ae-video-iframe',
      title: this.spec.titulo ?? 'Vídeo',
      attrs: {
        allow: 'autoplay; encrypted-media; picture-in-picture',
        allowfullscreen: '',
        referrerpolicy: 'strict-origin-when-cross-origin',
        loading: 'lazy'
      }
    });
    iframe.src = embedUrl(this.spec.video);
    frame.append(iframe);
    return frame;
  }

  private buildModal(): El<'div'> {
    const overlay = el('div', {
      className: 'ae-video-overlay',
      attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': this.spec.titulo ?? 'Vídeo' }
    });
    overlay.append(
      el('div', {
        className: 'ae-video-backdrop',
        onclick: () => this.close()
      })
    );

    const head = el('div', { className: 'ae-video-head' }, [
      el('h2', { className: 'ae-video-title', textContent: this.spec.titulo ?? 'Vídeo' }),
      button('✕', () => this.close(), { className: 'ae-video-close', ariaLabel: 'Cerrar el vídeo' })
    ]);

    const panel = el('div', { className: 'ae-video-panel' }, [head, this.buildPlayer()]);
    if (this.spec.descripcion) {
      panel.append(el('p', { className: 'ae-video-desc', textContent: this.spec.descripcion }));
    }
    overlay.append(panel);
    return overlay;
  }

  private buildTv(): El<'div'> {
    const tv = el('div', {
      className: 'ae-video-tv',
      attrs: { role: 'dialog', 'aria-label': this.spec.titulo ?? 'Vídeo en el escenario' }
    });
    tv.append(
      this.buildPlayer(),
      button('✕', () => this.close(), { className: 'ae-video-tv-close', ariaLabel: 'Cerrar el vídeo' })
    );
    return tv;
  }

  /* --------------------------------- anclaje -------------------------------- */

  private startAnchorLoop(): void {
    const step = (): void => {
      this.rafId = requestAnimationFrame(step);
      const tv = this.root;
      const objeto = this.spec.objeto;
      if (!tv || !objeto || !this.opts.anchor) return;
      const rect = this.opts.anchor(objeto, this.spec.ancho);
      if (!rect) {
        tv.style.visibility = 'hidden';
        return;
      }
      tv.style.visibility = 'visible';
      tv.style.left = `${rect.left}px`;
      tv.style.top = `${rect.top}px`;
      tv.style.width = `${rect.width}px`;
      tv.style.height = `${rect.height}px`;
    };
    this.rafId = requestAnimationFrame(step);
  }
}
